import { useMe } from '../shell/me-context.js';
import { NotFoundPage, PermissionDeniedPage } from '../shell/pages.js';
import { Page, PageHead, StateView } from '../ui/index.js';
import { CoverageSection } from './executive-coverage.js';
import { MattersSection } from './executive-matters.js';
import { ExecutiveStrip, type ExecutiveSectionId } from './executive-parts.js';
import { PulseSection } from './executive-pulse.js';
import { ReportSection } from './executive-report.js';
import { EXECUTIVE_DENIED_WHAT, EXECUTIVE_OFF_DESCRIPTION, EXECUTIVE_OFF_TITLE } from './lib/executive.js';
import './pages.css';

// The executive module (ADR 0019; design §2.13; the owner's picks E11 and E12, and his ruling on §5.16, "Executive
// fifth"): one destination, four URL-addressed sections — Report, Pulse, Matters, Coverage — carried by a section strip
// (02-patterns §2.4): links with aria-current, never a tablist, after the page head and before the body, identical on
// desktop and mobile, and rendered on every route of the module, its dead ends included (the AdminRoute shape).
//
// Selection only (permission-model P8): the page is offered to a person /api/me says holds `executive_intelligence` under
// its floor; every route answers for itself through `requireExecutive`, and a refusal there is rendered as the state it
// is — denied, or not configured — never as an empty report. Each section reads its own data when it is shown, and only
// then: the server audits each executive's first open of a report, every download, and the day's first read of each view
// (ADR 0019, "Audit each read"), so nothing is prefetched for a section the reader has not opened.

export type { ExecutiveSectionId } from './executive-parts.js';

/** The module's dead end for a floor that is off: the page head, the strip (holding nothing), and the not-configured state. */
function ExecutiveOffPage() {
  return (
    <Page measure="reading">
      <PageHead title="Executive" strip={<ExecutiveStrip />} />
      <StateView kind="not-configured" title={EXECUTIVE_OFF_TITLE} description={EXECUTIVE_OFF_DESCRIPTION} minHeight="40dvh" />
    </Page>
  );
}

/**
 * One route of the module. Not configured (the floor off, for everyone) comes before not permitted (05-client §5.4); a
 * reader who does not hold the capability gets the permission-denied page with the strip of the sections they hold.
 */
export function ExecutiveModule({ section }: { section: ExecutiveSectionId }) {
  const me = useMe();
  if (me.integrations.executive === 'off') return <ExecutiveOffPage />;
  if (!me.capabilities.executiveIntelligence) return <PermissionDeniedPage what={EXECUTIVE_DENIED_WHAT} strip={<ExecutiveStrip />} />;
  switch (section) {
    case 'pulse':
      return <PulseSection />;
    case 'matters':
      return <MattersSection />;
    case 'coverage':
      return <CoverageSection />;
    case 'report':
    default:
      return <ReportSection />;
  }
}

/** An unknown address under /executive still carries the strip (02-patterns §2.4). */
export function ExecutiveNotFound() {
  return <NotFoundPage strip={<ExecutiveStrip />} />;
}
